import axios from 'axios';

// Default fallback URL
const DEFAULT_URL = 'https://chatbot.piosphere.ai/api';
let currentEndpoint = DEFAULT_URL;

export const api = axios.create({
  baseURL: DEFAULT_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

export const setApiEndpoint = (endpoint) => {
  if (endpoint) {
    currentEndpoint = `https://${endpoint}/api`;
    api.defaults.baseURL = currentEndpoint;
    return true;
  }
  return false;
};

export const setApiKey = (key) => {
  if (key) {
    api.defaults.headers['x-api-key'] = key;
    return true;
  }
  return false;
};

export const validateApiKey = async (key, endpoint) => {
  try {
    if (!key) {
      throw new Error('API key is missing');
    }

    const url = endpoint ? `https://${endpoint}/api/key/validate` : `${currentEndpoint}/key/validate`;

    const response = await axios.post(url, 
      { key },
      { headers: { 'x-api-key': key, 'Content-Type': 'application/json' } }
    );

    if (response.status !== 200 || !response.data.data.is_valid) {
      console.error('Invalid API key');
      return false;
    }

    return response.data.data;
  } catch (error) {
    console.error('Validation error:', error);
    return false;
  }
};

export const getModelsHealth = async (apiKey, endpoint) => {
  try {
    if (!apiKey) {
      throw new Error('API key is missing');
    }

    const url = endpoint ? `https://${endpoint}/api/models/health` : `${currentEndpoint}/models/health`;

    const response = await axios.get(url, {
      headers: { 'x-api-key': apiKey }
    });

    if (response.status !== 200) {
      console.error('Failed to retrieve models health');
      return false;
    }

    return response.data.data;
  } catch (error) {
    console.error('Models health error:', error);
    return false;
  }
};

export const generateResponse = async ({ query, model, apiKey, endpoint }) => {


  if (!apiKey) {
    throw new Error('API key is missing');
  }
  
  const url = endpoint ? `https://${endpoint}/api/generate` : `${currentEndpoint}/generate`;

  const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey
      },
      body: JSON.stringify({ 
          query,
          model,
          domain: "main"
      })
  });

  if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response;
};

export const ingestFile = async (files, apiKey, endpoint) => {
    
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const formData = new FormData();
    
    if (Array.isArray(files)) {
        files.forEach(file => {
            formData.append('files', file);
        });
    } else {
        formData.append('files', files);
    }
    
    formData.append('domain', 'main');
    
    const url = endpoint ? `https://${endpoint}/api/file/ingest` : `${currentEndpoint}/file/ingest`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'x-api-key': apiKey,
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const generateQuery = async ({ query, model, apiKey, endpoint }) => {


    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/generate` : `${currentEndpoint}/generate`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({ 
            query,
            model,
            domain: "main"
        })
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
};

export const getFiles = async (apiKey, page = 1, endpoint) => {

    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/file?domain=main&page=${page}` : `${currentEndpoint}/file?domain=main&page=${page}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const getFile = async (fileId, apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/file/${fileId}` : `${currentEndpoint}/file/${fileId}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const deleteFile = async (fileId, apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/file/${fileId}` : `${currentEndpoint}/file/${fileId}`;

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'x-api-key': apiKey,
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete file: ${response.status}`);
    }
    return response.json();
};

export const createSession = async (title = 'New Chat', apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/session` : `${currentEndpoint}/session`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey
        },
        body: JSON.stringify({ title })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to create session: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    if (!responseData.data?.session_id) {
        throw new Error('Invalid session creation response');
    }
    
    return {
        sessionId: responseData.data.session_id,
        ...responseData.data
    };
};

export const sendChatMessage = async (sessionId, message, model, apiKey, audioFile = null) => {
    if (!sessionId) {
        throw new Error('Valid session ID is required for chat messages');
    }
    
    if (!model) {
        throw new Error('Model is required');
    }

    if (!apiKey) {
      throw new Error('API key is missing');
    }

    const formData = new FormData();
    formData.append('session', sessionId);
    formData.append('message', message);
    formData.append('domain', 'main');
    formData.append('model', model);
    
    if (audioFile) {
        formData.append('audio', audioFile);
    }

    const response = await fetch(`${currentEndpoint}/chat`, {
        method: 'POST',
        headers: {
            'Accept': 'text/event-stream',
            'x-api-key': apiKey
        },
        body: formData
    });

    if (response.status === 404) {
        throw new Error('Session expired or invalid');
    }

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Chat request failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const stream = new ReadableStream({
        async start(controller) {
            try {
                while (true) {
                    const {done, value} = await reader.read();
                    if (done) {
                        controller.close();
                        break;
                    }
                    controller.enqueue(value);
                }
            } catch (error) {
                controller.error(error);
            }
        }
    });

    return new Response(stream, {
        headers: {
            'Content-Type': 'text/event-stream'
        }
    });
};

export const getModels = async (apiKey, endpoint) => {
  try {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/models` : `${currentEndpoint}/models`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'x-api-key': apiKey
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    // The API now returns data in a different format, handle both formats
    return result.data ? result : { data: result.data || result };
  } catch (error) {
    console.error('Error fetching models:', error);
    throw error;
  }
};

export const sendMessageFeedback = async (messageId, isGood, apiKey) => {
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  const response = await fetch(`${currentEndpoint}/message`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey
    },
    body: JSON.stringify({
      message_id: messageId,
      is_good: isGood
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Feedback submission failed: ${response.status}`);
  }
  
  return response.json();
};

export const getApiKeys = async (apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/keys` : `${currentEndpoint}/keys`;
  
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
};

export const createApiKey = async ({ name, role, expiry, rate_limit }, apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const validatedData = {
        name,
        role,
        expiry: parseInt(expiry, 10),
        rate_limit: parseInt(rate_limit, 10)
    };

    if (!name || typeof name !== 'string') {
        throw new Error('Name is required and must be a string');
    }

    if (!Number.isInteger(validatedData.expiry) || validatedData.expiry <= 0) {
        throw new Error('Expiry must be a positive integer');
    }

    if (!Number.isInteger(validatedData.rate_limit) || 
        validatedData.rate_limit <= 0 || 
        validatedData.rate_limit > 50) {
        throw new Error('Rate limit must be an integer between 1 and 50');
    }
    
    const url = endpoint ? `https://${endpoint}/api/keys` : `${currentEndpoint}/keys`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey
        },
        body: JSON.stringify(validatedData)
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.data;
};

export const deleteApiKey = async (keyId, apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/keys/${keyId}` : `${currentEndpoint}/keys/${keyId}`;
  
    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'x-api-key': apiKey
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const revokeApiKey = async (keyId, apiKey) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
  
    const response = await fetch(`${currentEndpoint}/keys/${keyId}/revoke`, {
        method: 'POST',
        headers: {
            'x-api-key': apiKey
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const updateApiKey = async (keyId, updates, apiKey) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
  
    const response = await fetch(`${currentEndpoint}/keys/${keyId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey
        },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const getKeyUsageStats = async (keyId, apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/key/${keyId}/stats` : `${currentEndpoint}/key/${keyId}/stats`;
  
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey
        }
    });
    
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const getKeyUsageByDateRange = async (keyId, startDate, endDate, groupByKey = true, apiKey) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
  
    const queryParams = new URLSearchParams({
        key_id: keyId,
        start_date: startDate,
        end_date: endDate,
        group_by_key: groupByKey
    }).toString();

    const response = await fetch(`${currentEndpoint}/key/by-date?${queryParams}`, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const getAvailableEndpoints = async () => {
    try {
        const response = await axios.get('https://api.v1.piosphere.ai/agent-manager/public/v1/endpoints');
        
        if (response.status !== 200) {
            throw new Error('Failed to retrieve available endpoints');
        }
        
        return response.data;
    } catch (error) {
        console.error('Error fetching available endpoints:', error);
        throw error;
    }
};

export const crawlWebsite = async (crawlData, apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/file/crawl` : `${currentEndpoint}/file/crawl`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
        },
        body: JSON.stringify({
            url: crawlData.url,
            domain: crawlData.domain || 'main',
            max_page: crawlData.max_page || 100,
            max_depth: crawlData.max_depth || 3,
            crawl_sub_domain: crawlData.crawl_sub_domain !== false
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to start crawling: ${response.status}`);
    }
    return response.json();
};

export const getUploadStatus = async (uploadId, apiKey, endpoint) => {
    if (!apiKey) {
      throw new Error('API key is missing');
    }
    
    const url = endpoint ? `https://${endpoint}/api/file/upload/${uploadId}` : `${currentEndpoint}/file/upload/${uploadId}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to get upload status: ${response.status}`);
    }
    return response.json();
};
